import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Search, Users, AlertTriangle, CheckCircle, ArrowRight, ArrowLeft, DollarSign, History, UserCheck } from 'lucide-react';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useCompensationAssignments, useCompanyCompensationRules } from '@/hooks/compensation';
import { toast } from '@/hooks/use-toast';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { CompensationRule } from '@/services/compensation/types';

interface BulkAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRule?: CompensationRule | null;
}

export function BulkAssignmentDialog({
  open,
  onOpenChange,
  selectedRule,
}: BulkAssignmentDialogProps) {
  const [currentStep, setCurrentStep] = useState<'select-rule' | 'select-employees'>('select-rule');
  const [selectedRuleFromDialog, setSelectedRuleFromDialog] = useState<CompensationRule | null>(null);
  const [ruleSearchQuery, setRuleSearchQuery] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Determine which rule to use and what step to start on
  const activeRule = selectedRule || selectedRuleFromDialog;
  const isRuleProvided = !!selectedRule;

  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const { data: employees, isLoading: employeesLoading } = useCompanyEmployees();
  const { data: companyRules, isLoading: rulesLoading } = useCompanyCompensationRules();
  const { 
    useRuleAssignments, 
    useAssignRule
  } = useCompensationAssignments();

  // Get existing assignments for the active rule
  const { data: existingAssignments } = useRuleAssignments(activeRule?.id || '');
  
  // Get assignment mutation
  const assignRule = useAssignRule();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (isRuleProvided) {
        // When opening from individual rule's bulk button, skip to employee selection
        setCurrentStep('select-employees');
      } else {
        // When opening from general bulk assignment button, start at rule selection
        setCurrentStep('select-rule');
        setSelectedRuleFromDialog(null);
      }
      setSelectedEmployees([]);
      setSearchQuery('');
      setRuleSearchQuery('');
    }
  }, [open, isRuleProvided]);

  // Filter company rules based on search
  const filteredRules = companyRules?.filter((rule: CompensationRule) =>
    rule.rule_name.toLowerCase().includes(ruleSearchQuery.toLowerCase()) ||
    rule.rule_type.toLowerCase().includes(ruleSearchQuery.toLowerCase()) ||
    (rule.description && rule.description.toLowerCase().includes(ruleSearchQuery.toLowerCase()))
  ) || [];

  // Get currently assigned employee IDs
  const assignedEmployeeIds = existingAssignments?.map(assignment => assignment.profile_id) || [];
  
  const filteredEmployees = employees?.filter((employee: any) =>
    `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Check for conflicts (employees who would get duplicate assignments)
  const conflictingEmployees = selectedEmployees.filter(id => assignedEmployeeIds.includes(id));
  const newAssignments = selectedEmployees.filter(id => !assignedEmployeeIds.includes(id));

  const getEmployeeInitials = (employee: any) => {
    return `${employee.first_name?.[0] || ''}${employee.last_name?.[0] || ''}`.toUpperCase();
  };

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Skip already assigned employees from selection
  const handleSelectAll = () => {
    const unassignedEmployees = filteredEmployees.filter((emp: any) => !assignedEmployeeIds.includes(emp.id));
    if (selectedEmployees.length === unassignedEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(unassignedEmployees.map((emp: any) => emp.id));
    }
  };

  const handleRuleSelect = (rule: CompensationRule) => {
    setSelectedRuleFromDialog(rule);
    setCurrentStep('select-employees');
  };

  const handleBackToRuleSelection = () => {
    setCurrentStep('select-rule');
    setSelectedEmployees([]);
  };

  const handleBulkAssign = async () => {
    if (!activeRule || newAssignments.length === 0) return;

    setIsAssigning(true);
    try {
      // Use the new assignment system instead of creating duplicate rules
      await assignRule.mutateAsync({
        ruleId: activeRule.id,
        profileIds: newAssignments,
      });
      
      toast({
        title: "Bulk Assignment Successful",
        description: `Assigned ${activeRule.rule_name} to ${newAssignments.length} new employees.`,
      });
      
      onOpenChange(false);
      setSelectedEmployees([]);
    } catch (error) {
      toast({
        title: "Assignment Failed",
        description: error instanceof Error ? error.message : "Failed to assign compensation rule to employees.",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            Bulk Assignment
            <span className="text-sm font-normal text-muted-foreground ml-2">
              - Step {currentStep === 'select-rule' ? '1' : '2'} of 2
            </span>
          </DialogTitle>
          <DialogDescription>
            {currentStep === 'select-rule' 
              ? "Select a compensation rule to assign to multiple employees"
              : `Assign "${activeRule?.rule_name}" to multiple employees at once`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto pr-2 space-y-6">
          {/* Step 1: Rule Selection */}
          {currentStep === 'select-rule' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Select Compensation Rule</Label>
                <Badge variant="secondary">
                  {filteredRules.length} rule{filteredRules.length !== 1 ? 's' : ''} available
                </Badge>
              </div>

              {/* Rule Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search rules by name, type, or description..."
                  value={ruleSearchQuery}
                  onChange={(e) => setRuleSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Rules List */}
              <div className="border rounded-lg flex-1 overflow-y-auto">
                {rulesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : filteredRules.length > 0 ? (
                  <div className="space-y-2 p-4">
                    {filteredRules.map((rule: CompensationRule) => (
                      <Card
                        key={rule.id}
                        className="cursor-pointer transition-colors hover:bg-accent/50 border-2 hover:border-primary/20"
                        onClick={() => handleRuleSelect(rule)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-medium">{rule.rule_name}</h4>
                                <Badge variant={rule.rule_type === 'deduction' ? 'destructive' : 'default'}>
                                  {rule.rule_type.replace('_', ' ').toUpperCase()}
                                </Badge>
                              </div>
                              {rule.description && (
                                <p className="text-sm text-muted-foreground mb-2">{rule.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span>Method: {rule.calculation_method.replace('_', ' ')}</span>
                                <span>Priority: {rule.priority_order}</span>
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-lg font-semibold">
                                {rule.calculation_method === 'fixed_amount' && formatCurrency(rule.base_rate || 0)}
                                {rule.calculation_method === 'percentage' && `${rule.percentage_rate}%`}
                                {rule.calculation_method === 'multiplier' && `${rule.multiplier_rate}x`}
                              </div>
                              <ArrowRight className="h-4 w-4 text-muted-foreground mt-1 ml-auto" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                    <DollarSign className="h-8 w-8 mb-2" />
                    <p>No compensation rules found</p>
                    <p className="text-xs">Try adjusting your search query</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Employee Selection */}
          {currentStep === 'select-employees' && (
            <>
              {/* Rule Summary */}
              {activeRule && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Selected Rule</CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBackToRuleSelection}
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Change Rule
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{activeRule.rule_name}</p>
                        <p className="text-sm text-muted-foreground">{activeRule.description}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant={activeRule.rule_type === 'deduction' ? 'destructive' : 'default'}>
                          {activeRule.rule_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-1">
                          {activeRule.calculation_method === 'fixed_amount' && formatCurrency(activeRule.base_rate || 0)}
                          {activeRule.calculation_method === 'percentage' && `${activeRule.percentage_rate}%`}
                          {activeRule.calculation_method === 'multiplier' && `${activeRule.multiplier_rate}x`}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Employee Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Label className="text-base font-medium">Select Employees</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {selectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <Badge variant="secondary">
                    {selectedEmployees.length} of {filteredEmployees.length} selected
                  </Badge>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Assignment Status */}
                {existingAssignments && existingAssignments.length > 0 && (
                  <Card className="mb-4">
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        Current Assignments ({existingAssignments.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground">
                        This rule is already assigned to {existingAssignments.length} employee{existingAssignments.length !== 1 ? 's' : ''}.
                        Selecting already assigned employees will skip them.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Conflicts Warning */}
                {conflictingEmployees.length > 0 && (
                  <Card className="border-warning mb-4">
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2 text-warning">
                        <AlertTriangle className="h-4 w-4" />
                        Assignment Conflicts ({conflictingEmployees.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground">
                        {conflictingEmployees.length} selected employee{conflictingEmployees.length !== 1 ? 's are' : ' is'} already assigned this rule and will be skipped.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Employee List */}
                <div className="border rounded-lg flex-1 overflow-y-auto">
                  {employeesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : filteredEmployees.length > 0 ? (
                    <div className="space-y-2 p-4">
                      {filteredEmployees.map((employee: any) => {
                        const isAssigned = assignedEmployeeIds.includes(employee.id);
                        const isConflict = conflictingEmployees.includes(employee.id);
                        
                        return (
                          <div
                            key={employee.id}
                            className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                              isAssigned ? 'bg-muted/50' : 'hover:bg-accent/50 cursor-pointer'
                            }`}
                            onClick={() => !isAssigned && handleEmployeeToggle(employee.id)}
                          >
                            <Checkbox
                              checked={selectedEmployees.includes(employee.id)}
                              onChange={() => !isAssigned && handleEmployeeToggle(employee.id)}
                              disabled={isAssigned}
                            />
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={employee.avatar_url || ''} />
                              <AvatarFallback className="text-xs">
                                {getEmployeeInitials(employee)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className={`text-sm font-medium ${isAssigned ? 'text-muted-foreground' : ''}`}>
                                  {employee.first_name} {employee.last_name}
                                </p>
                                {isAssigned && (
                                  <Badge variant="secondary" className="text-xs">
                                    Already Assigned
                                  </Badge>
                                )}
                                {isConflict && (
                                  <Badge variant="destructive" className="text-xs">
                                    Conflict
                                  </Badge>
                                )}
                              </div>
                              <p className={`text-xs ${isAssigned ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                                {employee.email} • {employee.role || 'Employee'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <Users className="h-8 w-8 mb-2" />
                      <p>No employees found</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t flex-shrink-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {currentStep === 'select-rule' ? (
              <>
                <AlertTriangle className="h-4 w-4" />
                <span>Select a rule to continue to employee selection</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>
                  {newAssignments.length > 0 
                    ? `Will assign rule to ${newAssignments.length} new employee${newAssignments.length !== 1 ? 's' : ''}`
                    : conflictingEmployees.length > 0
                      ? "All selected employees already have this rule assigned"
                      : "Select employees to assign this rule"
                  }
                </span>
              </>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {currentStep === 'select-employees' && (
              <Button 
                onClick={handleBulkAssign}
                disabled={newAssignments.length === 0 || isAssigning}
              >
                {isAssigning ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                {newAssignments.length > 0 
                  ? `Assign to ${newAssignments.length} New Employee${newAssignments.length !== 1 ? 's' : ''}`
                  : 'No New Assignments'
                }
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}